/* eslint class-methods-use-this: 0 */
import {
  find, flatMapDeep, map, uniq,
} from 'lodash';
import CollectionApi from 'client/api/v2/CollectionApi';
import PolicyTemplateApi from 'client/api/v2/PolicyTemplateApi';
import ProvidersApi from 'client/api/v2/ProvidersApi';
import axios from 'axios';

const allowedSearchFields = [
  'name',
];

const allowedMemberOperations = ['ADD', 'REMOVE'];

const apiUrl = '/api/v2/providers/{pid}/policygrouptemplates';

class CustomPolicyGroupTemplatesApi extends CollectionApi {
  constructor(providerId = '') {
    const url = apiUrl.replace('{pid}', providerId);

    super(url, {
      skip: 0,
      limit: 10,
      sort: 'name',
      fields: [
        'name',
        'description',
        'members',
      ],
      filter: [{ name: 'name', operator: 'search', value: '' }],
      search: {
        fields: allowedSearchFields,
        searchTerm: '',
      },
    });

    this.providerId = providerId;
    // Default will hydrate members with template details.
    this.willHydrateMembers = true;
  }

  async fetch(skip, limit, sort, searchTerm, cancelToken, filters = []) {
    if (!this.providerId) {
      throw new Error('Missing Provider ID');
    }

    const listResponse = await super.fetch(
      skip, limit, sort, searchTerm, cancelToken, filters,
    );

    let templates = listResponse.records;
    if (this.willHydrateMembers && listResponse.records) {
      templates = await this.setPolicyGroupTemplateMemberDetails(
        listResponse.records,
      );
    }

    const results = {
      totalCount: listResponse.totalCount,
      results: templates,
    };

    return results;
  }

  /**
   * Retrieve a list of Configured Policy Template (members) for a Policy Group
   * Template.
   * @param {string} id The Policy Group Template ID
   * @returns Object containing a count and collection of policy group template
   * members.
   */
  async fetchBoundPolicies(templateId, args = {}) {
    if (!templateId) {
      throw new Error('Missing Policy Group Template ID');
    }

    const {
      skip = 0,
      limit = 10,
      sort = 'name',
      searchTerm = '',
    } = args;

    const membersUrl = `${this.baseUrl}/${templateId}/members`;

    const urlParams = {
      params: {
        skip,
        limit,
        sort,
      },
    };

    if (searchTerm !== '') {
      urlParams.params.filter = `name:search:${searchTerm}`;
    }

    const res = await this.axios.get(membersUrl, urlParams);
    const { totalCount, records } = res.data;

    // Hydrate the records with Template information
    const members = await this.setConfiguredPolicyTemplateDetails(records);

    return {
      totalCount,
      results: members,
    };
  }

  performMemberOperation(templateId, memberId, operation) {
    if (!templateId) throw new Error('Missing template id');
    if (!memberId) throw new Error('Missing member id');
    if (!operation) throw new Error('Missing member operation');

    if (!allowedMemberOperations.includes(operation)) {
      throw new Error('Invalid member operation');
    }

    const payload = {
      id: memberId,
      operation,
    };

    const membersUrl = `${this.baseUrl}/${templateId}/members`;
    return this.axios.post(membersUrl, payload);
  }

  removeMember(templateId, memberId) {
    return this.performMemberOperation(templateId, memberId, 'REMOVE');
  }

  addMember(templateId, memberId) {
    return this.performMemberOperation(templateId, memberId, 'ADD');
  }

  setProviderId(providerId) {
    this.baseUrl = apiUrl.replace('{pid}', providerId);
    this.providerId = providerId;
  }

  get(id, cancelToken) {
    const mesg = 'Unable to get item!';
    const Api = ProvidersApi;

    return new Promise((resolve, reject) => {
      Api.initialize()
        .then(() => {
          axios
            .get(`${this.baseUrl}/${id}`, { cancelToken })
            .then((resp) => {
              if (resp && resp.data) {
                resolve(resp.data);
              } else {
                Api.handleError(reject, mesg);
              }
            })
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  async setConfiguredPolicyTemplateDetails(configuredPolicyTemplates) {
    // eslint-disable-next-line no-undef
    const cfgPolicies = _.clone(configuredPolicyTemplates);
    const pTemplateIds = uniq(map(cfgPolicies, 'policyTemplateId'));
    const pTemplates = await this.getPolicyTemplatesById(pTemplateIds);

    cfgPolicies.forEach((cfgPolicy, cfgPolicyKey) => {
      const template = find(pTemplates.results, { id: cfgPolicy.policyTemplateId });
      if (template) {
        cfgPolicies[cfgPolicyKey] = { ...cfgPolicy, template, selected: true };
      }
    });

    return cfgPolicies;
  }

  async setPolicyGroupTemplateMemberDetails(policyGroupTemplates) {
    // Convert all Member properties to Policies property.
    const pgTemplates = map(policyGroupTemplates, (item) => {
      const model = { ...item, policies: item.members };
      delete model.members;
      return model;
    });

    const pTemplateIds = uniq(map(flatMapDeep(pgTemplates, 'policies'), 'policyTemplateId'));
    const pTemplates = await this.getPolicyTemplatesById(pTemplateIds);

    pgTemplates.forEach((group, groupKey) => {
      group.policies.forEach((policy, policyKey) => {
        const template = find(pTemplates.results, { id: policy.policyTemplateId });

        if (template) {
          pgTemplates[groupKey].policies[policyKey] = { ...policy, template };
        }
      });
    });

    // Remove policies that don't have a template
    pgTemplates.forEach((group) => {
      group.policies.forEach((policy, policyKey) => {
        if (!policy.template) {
          group.policies.splice(policyKey, 1);
        }
      });
    });

    return pgTemplates;
  }

  async getPolicyTemplatesById(values) {
    return PolicyTemplateApi
      .findPolicyTemplatesById(values, { limit: values.length });
  }

  setHydrateMembers(willHydrate) {
    this.willHydrateMembers = willHydrate;
  }
}

export default new CustomPolicyGroupTemplatesApi();
